<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <h1 class="font-bold mt-2 text-5xl">Validações</h1>
      </v-container>
      <v-container class="onest-font d-flex justify-end gap-2">
        <v-btn
          to="/create-validation-period"
          variant="flat"
          prepend-icon="mdi-calendar-check"
          color="primary"
        >
          Criar periodo de validação
        </v-btn>
      </v-container>
    </div>
    <v-container>
      <div class="search-bar w-full pb-4">
        <v-text-field
          v-model="searchQuery"
          variant="solo"
          flat
          hide-details
          class="search-input"
          placeholder="Buscar validação por nome ou status..."
          aria-label="Buscar validação por nome ou status..."
          append-inner-icon="mdi-magnify"
        ></v-text-field>
      </div>

      <!-- Menu de Filtros por Status -->
      <div class="status-filters w-full pb-4 onest-font">
        <v-chip-group
          v-model="selectedStatus"
          column
          mandatory
          selected-class="bg-primary text-white"
        >
          <v-chip value="TODAS" variant="outlined" class="font-medium">
            Todas
          </v-chip>
          <v-chip
            v-for="(label, value) in statusOptions"
            :key="value"
            :value="value"
            variant="outlined"
            class="font-medium"
          >
            {{ label }}
          </v-chip>
        </v-chip-group>
      </div>

      <div v-if="loading" class="py-12 text-center text-gray-500 font-medium">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <p>Carregando validações...</p>
      </div>

      <div v-else>
        <v-row v-if="filteredValidations.length > 0">
          <v-col>
            <div class="my-4">
              <table class="w-full border-separate border-spacing-y-2">
                <thead class="p-2">
                  <tr class="">
                    <th class="p-4 font-medium text-left">Validação</th>
                    <th class="p-4 font-medium text-left">
                      Projetos envolvidos
                    </th>
                    <th class="p-4 font-medium text-left">Status</th>
                    <th class="p-4 font-medium text-left">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="validation in filteredValidations"
                    :key="validation.id"
                    class="hover:bg-blue-50/50 transition-colors bg-white shadow-sm"
                  >
                    <td
                      class="py-4 px-6 font-normal text-gray-600 rounded-l-xl"
                    >
                      {{ validation.nome }}
                    </td>
                    <td class="py-4 px-6 text-gray-600">
                      {{ validation.projetos.map((p) => p.nome).join(', ') }}
                    </td>
                    <td class="py-4 px-2">
                      <v-chip
                        :color="statusChipColor(validation.status)"
                        class="font-normal"
                        size="small"
                        variant="tonal"
                      >
                        {{ formatarStatusValidacao(validation.status) }}
                      </v-chip>
                    </td>
                    <td class="py-4 px-2 rounded-r-xl">
                      <v-tooltip
                        v-if="validation.status !== 'EDICAO' && isCoordenador"
                        location="bottom"
                        text="Visualizar"
                      >
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            :to="'/validation-details/' + validation.id"
                            color="grey-darken-1"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full mr-2"
                          >
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip
                        v-if="validation.status === 'EDICAO'"
                        location="bottom"
                        text="Editar"
                      >
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            :to="'/create-validation-period/' + validation.id"
                            color="primary"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip
                        v-if="validation.status === 'EDICAO' && isCoordenador"
                        location="bottom"
                        text="Excluir"
                      >
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            color="error"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full ml-2"
                            @click="confirmarDeletarValidacao(validation)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col class="text-center py-8 text-gray-500">
            Nenhuma validação encontrada para o filtro selecionado.
          </v-col>
        </v-row>
      </div>

      <!-- Modal de Confirmação de Exclusão -->
      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card class="onest-font p-4">
          <v-card-title class="text-h5 font-bold"
            >Confirmar Exclusão</v-card-title
          >
          <v-card-text>
            Tem certeza de que deseja excluir a validação "<strong>{{
              validationToDelete?.nome
            }}</strong
            >"? Esta ação não pode ser desfeita.
          </v-card-text>
          <v-card-actions class="d-flex justify-end gap-2 mt-4">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="closeDeleteDialog"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="error"
              variant="flat"
              :loading="deleting"
              @click="deletarValidacaoConfirmada"
            >
              Excluir
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
/**
 * @module TabelaValidacoes
 * @description Componente que renderiza a tabela de validações.
 * @component TabelaValidacoes
 * @property {string} searchQuery - Query de busca.
 * @property {Array} validacoes - Array de validações.
 * @property {boolean} loading - Status de carregamento.
 */
import axios from 'axios';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  components: {},
  data() {
    return {
      searchQuery: '',
      validacoes: [],
      loading: false,
      selectedStatus: 'TODAS',
      deleteDialog: false,
      validationToDelete: null,
      deleting: false,
      statusOptions: {
        EDICAO: 'Em Edição',
        PREPARACAO: 'Em Preparação',
        VALIDACAO_ROTEIROS: 'Validação de Roteiros',
        USUARIOS_TESTANDO: 'Usuários Testando',
        IDENTIFICACAO_PENDENCIAS: 'Identificação de Pendências',
        PRIORIZACAO_PENDENCIAS: 'Priorização de Pendências',
        CORRECOES: 'Correções',
        VALIDACAO_CORRECOES: 'Validação de Correções',
        VALIDACAO_FINALIZADA: 'Validação Finalizada',
      },
    };
  },
  computed: {
    /**
     * @computed
     * @description Verifica se o usuário atual é coordenador
     * @returns {boolean} True se for coordenador
     */
    isCoordenador() {
      return localStorage.getItem('funcao') === 'Coordenador';
    },
    /**
     * @computed
     * @description Filtra as validações baseado na query de busca e no status selecionado.
     * @returns {Array} Array de validações filtradas.
     */
    filteredValidations() {
      let result = this.validacoes;

      // 1. Filtrar por query de busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter((validacao) => {
          const nomeMatches = validacao.nome.toLowerCase().includes(query);
          const projetosMatches = validacao.projetos.some((projeto) =>
            projeto.nome.toLowerCase().includes(query),
          );
          const rawStatusMatches = validacao.status
            .toLowerCase()
            .includes(query);

          // Buscar pelo status formatado (ex: "Em Edição", "Validação de Roteiros")
          const formattedStatus = this.formatarStatusValidacao(
            validacao.status,
          ).toLowerCase();
          const formattedStatusMatches = formattedStatus.includes(query);

          return (
            nomeMatches ||
            projetosMatches ||
            rawStatusMatches ||
            formattedStatusMatches
          );
        });
      }

      // 2. Filtrar por status selecionado
      if (this.selectedStatus && this.selectedStatus !== 'TODAS') {
        result = result.filter(
          (validacao) => validacao.status === this.selectedStatus,
        );
      }

      return result;
    },
  },
  mounted() {
    this.fetchValidacoes();
  },
  methods: {
    /**
     * @function
     * @description Busca as validações na API.
     * @returns {void}
     */
    async fetchValidacoes() {
      this.loading = true;
      try {
        const response = await axios.get(`${apiUrl}/api/v1/validacoes`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        this.validacoes = response.data.map((val) => ({
          id: val.id_validacao,
          nome: val.nome_validacao,
          projetos: val.tb_projetos_envolvidos.map((pe) => ({
            id: pe.id_projeto,
            nome: pe.tb_projeto
              ? pe.tb_projeto.nome
              : `Projeto ${pe.id_projeto}`,
          })),
          status: val.situacao,
        }));
      } catch (error) {
        console.error('Erro ao buscar validações:', error);
      } finally {
        this.loading = false;
      }
    },
    /**
     * @function
     * @description Obtém a cor do chip baseado no status da validação.
     * @param {string} status - Status da validação.
     * @returns {string} Cor do chip.
     */
    statusChipColor(status) {
      const colors = {
        EDICAO: '#9CA3AF',
        PREPARACAO: '#60A5FA',
        VALIDACAO_ROTEIROS: '#3B82F6',
        USUARIOS_TESTANDO: '#A78BFA',
        IDENTIFICACAO_PENDENCIAS: '#FBBF24',
        PRIORIZACAO_PENDENCIAS: '#F97316',
        CORRECOES: '#F87171',
        VALIDACAO_CORRECOES: '#34D399',
        VALIDACAO_FINALIZADA: '#10B981',
      };

      return colors[status];
    },
    /**
     * @function
     * @description Formata o status da validação.
     * @param {string} statusDoBanco - Status da validação no banco de dados.
     * @returns {string} Status formatado.
     */
    formatarStatusValidacao(statusDoBanco) {
      const mapaStatus = {
        EDICAO: 'Em Edição',
        PREPARACAO: 'Em Preparação',
        VALIDACAO_ROTEIROS: 'Validação de Roteiros',
        USUARIOS_TESTANDO: 'Usuários Testando',
        IDENTIFICACAO_PENDENCIAS: 'Identificação de Pendências',
        PRIORIZACAO_PENDENCIAS: 'Priorização de Pendências',
        CORRECOES: 'Correções',
        VALIDACAO_CORRECOES: 'Validação de Correções',
        VALIDACAO_FINALIZADA: 'Validação Finalizada',
      };

      return mapaStatus[statusDoBanco] || statusDoBanco;
    },
    /**
     * @method
     * @description Formata a data ISO para o formato DD/MM/AAAA.
     * @param {string} dataIso - Data em formato ISO.
     * @returns {string} Data formatada.
     */
    formatarData(dataIso) {
      if (!dataIso) {
        return '-';
      }
      const data = new Date(dataIso);

      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    confirmarDeletarValidacao(validation) {
      this.validationToDelete = validation;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.validationToDelete = null;
    },
    async deletarValidacaoConfirmada() {
      if (!this.validationToDelete) {
        return;
      }
      this.deleting = true;
      try {
        await axios.delete(
          `${apiUrl}/api/v1/validacoes/${this.validationToDelete.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );

        this.validacoes = this.validacoes.filter(
          (v) => v.id !== this.validationToDelete.id,
        );

        this.closeDeleteDialog();
      } catch (error) {
        console.error('Erro ao deletar validação:', error);
        alert('Ocorreu um erro ao excluir a validação.');
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
